import { ProjRoles, SiteRoles } from '../enums/roles';

export type AssignedScope = 'assigned-project' | 'assigned-site';

export interface ServerScopedGrant {
  readonly role: ProjRoles | SiteRoles;
  readonly capabilityId: string;
  readonly scope: AssignedScope;
  readonly reason: string;
}

const SITE_ROLES_READING_PROJECTS = [
  SiteRoles.SITE_ADMIN,
  SiteRoles.SITE_FIN_OFF,
  SiteRoles.SITE_FLD_USR,
  SiteRoles.SITE_PROC_OFF,
  SiteRoles.SITE_PROF,
] as const;

// Context: List Projects is deliberately absent. The Core API contract lets org members list
// projects without an assigned-resource restriction, so it resolves to `any` (PR #2410 review).
const PROJECT_READ_CAPABILITIES = ['projects.view-project', 'navigation.nav-projects'] as const;

const SITE_ROLE_PROJECT_REASON =
  'Unconditional READ PROJECT because a site role carries no project assignment; the API only serves a project containing one of the user’s sites (ability.factory.ts, site-role helpers).';

const PM_PROJECT_REASON =
  'Unconditional READ PROJECT so the Projects nav works; the API only serves the projects the PM manages (ability.factory.ts, applyProjectRoles).';

const PM_SITE_REASON =
  'Unconditional READ/LIST SITE because callers ask without an id; the API scopes the list to sites on the PM’s projects (ability.factory.ts, PLE-1317 Q4).';

/**
 * Grants the factory leaves unconditional only because the Core API scopes them.
 *
 * Context: probing these against a foreign project answers "yes", which would tell a site
 * admin they can see every project in the org. The resolver reports the assigned scope
 * listed here instead. Add an entry only alongside the factory comment it mirrors.
 */
export const SERVER_SCOPED: readonly ServerScopedGrant[] = [
  ...SITE_ROLES_READING_PROJECTS.flatMap((role) =>
    PROJECT_READ_CAPABILITIES.map((capabilityId) => ({
      role,
      capabilityId,
      scope: 'assigned-site' as const,
      reason: SITE_ROLE_PROJECT_REASON,
    })),
  ),
  ...PROJECT_READ_CAPABILITIES.map((capabilityId) => ({
    role: ProjRoles.PROJ_MNGR,
    capabilityId,
    scope: 'assigned-project' as const,
    reason: PM_PROJECT_REASON,
  })),
  ...(['sites.list-sites', 'sites.view-site'] as const).map((capabilityId) => ({
    role: ProjRoles.PROJ_MNGR,
    capabilityId,
    scope: 'assigned-project' as const,
    reason: PM_SITE_REASON,
  })),
];
